import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../firebase/auth.tsx";
import { checkForAdmin, getHeadquarterNameById } from "./helpers/requests.tsx";
import CreateHospitalModal from "./modals/createHospitalModal.tsx";
import ConfirmDeleteHQModal from "./modals/confirmDeleteHQModal.tsx";
import CreateAccountModal from "./modals/createAccountModal.tsx";
import ChangeHQDataModal from "./modals/changeHQDataModal.tsx";
import CreateLicenseModal from "./modals/createLicenseModal.tsx";
import CreateTrainingPlanModal from "./modals/createTrainingPlanModal.tsx";
import "../styles/admin.css";
import { correctUserRoleName } from "./helpers/helpers.tsx";
import { useTranslation } from "react-i18next";

const HeadquarterMenu = ({ updateHeadsets, updateHospitals, updateUserList, updateLicenseList, updateTrainingPlanList }) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { headquarterId } = params;

  const { userId, userRole } = useAuth();

  const { t, i18n } = useTranslation();

  const [headquarterName, setHeadquarterName] = useState("");
  const [showGlobalAdminBlock, setShowGlobalAdminBlock] = useState(false);
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);
  const [showCreateHospitalModal, setShowCreateHospitalModal] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [showConfirmDeleteHQModal, setShowConfirmDeleteHQModal] =
    useState(false);
  const [showCreateHQAccountModal, setShowCreateHQAccountModal] =
    useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const isAdmin = await checkForAdmin(userId);
      setShowGlobalAdminBlock(isAdmin.admin);

      const data = await getHeadquarterNameById(headquarterId);
      setHeadquarterName(data);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleChangeDataClick = () => {
    setIsOptionsOpen(false);
    setShowChangeDataModal(true);
  };

  const handleCreateHQAccountClick = () => {
    setIsOptionsOpen(false);
    setShowCreateHQAccountModal(true);
  };

  const handleDeleteHQClick = () => {
    setIsOptionsOpen(false);
    setShowConfirmDeleteHQModal(true);
  };

  const handleCreateHospitalClick = async () => {
    setIsOptionsOpen(false);
    setShowCreateHospitalModal(true);
  };

  const handleCreatePlanClick = () => {
    setShowCreatePlanModal(true);
  };

  const handleCreateHospital = (newHospital: any[]) => {
    updateHospitals(newHospital);
    setShowCreateHospitalModal(false);
    if (!location.pathname.includes("hospitalList")) {
      navigate(`/hospitalList/headquarter/${headquarterId}`);
    }
  };

  const handleCreatePlan = (newPlan: any) => {
    if (updateTrainingPlanList) updateTrainingPlanList(newPlan);
    setShowCreatePlanModal(false);
  };

  const handleDeleteHQSubmit = () => {
    navigate("/headquarterList");
  };

  const handleCreateHQAccount = (newAccount: any) => {
    updateUserList(newAccount);
  };

  const handleChangeData = async (newHQName: string) => {
    setHeadquarterName(newHQName);
    setShowChangeDataModal(false);
  };

  const renderChangeDataModal = () => {
    return (
      <div className={"adminContainer"}>
        <ChangeHQDataModal
          setShowChangeDataModal={setShowChangeDataModal} // Fixed prop name from setShowChangeLicenseModal to setShowChangeDataModal
          headquarterId={headquarterId}
          headquarterName={headquarterName}
          handleChangeData={handleChangeData}
        />
      </div>
    );
  };

  const renderCreateHospitalModal = () => {
    return (
      <div className={"adminContainer"}>
        <CreateHospitalModal
          setShowCreateHospitalModal={setShowCreateHospitalModal}
          headquarterId={headquarterId}
          handleCreateHospital={handleCreateHospital}
        />
      </div>
    );
  };

  const renderCreatePlanModal = () => {
    return (
      <div className={"adminContainer"}>
        <CreateTrainingPlanModal
          setShowCreatePlanModal={setShowCreatePlanModal}
          handleCreatePlan={handleCreatePlan}
          headquarterId={headquarterId}
        />
      </div>
    );
  };

  const renderConfirmDeleteHQModal = () => {
    return (
      <div className={"adminContainer"}>
        <ConfirmDeleteHQModal
          setShowConfirmDeleteHQModal={setShowConfirmDeleteHQModal}
          headquarterId={headquarterId}
          headquarterName={headquarterName}
          handleDeleteHQ={handleDeleteHQSubmit}
        />
      </div>
    );
  };

  const renderCreateHQAccountModal = () => {
    return (
      <div className={"adminContainer"}>
        <CreateAccountModal
          setShowCreateAccountModal={setShowCreateHQAccountModal}
          headsetId={""}
          headquarterId={headquarterId}
          hospitalId={""}
          userRole={"headquarterAdmin"}
          headquarterView={true}
          handleCreateAccount={handleCreateHQAccount}
        />
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          backgroundColor: "#D4E5E2",
          borderRadius: "10px",
          marginTop: "86px",
          marginLeft: "50px",
          marginRight: "50px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "70px",
        }}
      >
        {/* Left Side: Info + Navigation */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src="/images/Icon_HQ.png"
            alt="Logo"
            style={{
              width: "6vh",
              minWidth: "50px",
              marginLeft: "20px",
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginLeft: "20px",
              color: "white",
              lineHeight: "1",
            }}
          >
            <div
              style={{
                textAlign: "left",
                paddingBottom: "8px",
                color: "#39a686",
                fontSize: "1.3em",
              }}
            >
              <strong>{headquarterName}</strong>
            </div>
            <div
              style={{
                textAlign: "left",
                fontFamily: "VirtualLabQuickSandBold",
                color: "#212529",
                fontSize: "1.1em",
              }}
            >
              {t("Your_role")}:{" "}
              {
                <span style={{ fontFamily: "VirtualLabQuickSandMedium" }}>
                  {correctUserRoleName(userRole, t)}
                </span>
              }
            </div>
          </div>

          {/* Navigation Buttons moved to the left */}
          <div style={{ display: "flex", marginLeft: "40px" }}>
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/hospitalList/headquarter/${headquarterId}`)}
                style={{
                backgroundColor: location.pathname.includes("hospitalList") ? "#404040" : "#39A686",
                borderColor: location.pathname.includes("hospitalList") ? "#404040" : "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
                }}
            >
                {t("Hospitals")}
            </button>
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/headsets/headquarter/${headquarterId}`)}
                style={{
                backgroundColor: location.pathname.includes("headsets") ? "#404040" : "#39A686",
                borderColor: location.pathname.includes("headsets") ? "#404040" : "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
                }}
            >
                {t("Headsets")}
            </button>
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/licenseList/headquarter/${headquarterId}`)}
                style={{
                backgroundColor: location.pathname.includes("licenseList") ? "#404040" : "#39A686",
                borderColor: location.pathname.includes("licenseList") ? "#404040" : "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
                }}
            >
                {t("Licenses")}
            </button>
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/trainingPlanList/headquarter/${headquarterId}`)}
                style={{
                backgroundColor: location.pathname.includes("trainingPlanList") ? "#404040" : "#39A686",
                borderColor: location.pathname.includes("trainingPlanList") ? "#404040" : "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
                }}
            >
                {t("Training_Plans")}
            </button>
          </div>
        </div>

        {/* Right Side: Context Actions + Options */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginRight: "20px",
          }}
        >
          {location.pathname.includes("trainingPlanList") && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreatePlanClick}
              style={{
                backgroundColor: "#39A686",
                borderColor: "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
              }}
            >
              {t("Add_new_training_plan")}
            </button>
          )}
          {location.pathname.includes("hospitalList") && (
            <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateHospitalClick}
                style={{
                backgroundColor: "#39A686",
                borderColor: "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
                marginRight: "10px",
                }}
            >
                {t("Create_new_hospital")}
            </button>
          )}

          <div className="dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={toggleDropdown}
              style={{
                backgroundColor: "#39A686",
                borderColor: "#39A686",
                borderRadius: "10px",
                fontSize: "1.2em",
              }}
            >
              {t("Options")}
            </button>
            {isOptionsOpen && (
              <div className="dropdownMenu">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    handleChangeDataClick();
                  }}
                >
                  <img src="/images/Icon_Edit.png" />
                  {t("Edit_Headquarter")}
                </button>
                <button
                  onClick={handleCreateHQAccountClick}
                  type="button"
                  className="btn"
                >
                  <img src="/images/Icon_AddUser.png" />
                  {t("Create_new_User_account")}
                </button>
                {showGlobalAdminBlock && (
                  <button
                    onClick={handleDeleteHQClick}
                    type="button"
                    className="btn"
                  >
                    <img src="/images/Icon_Delete.png" />
                    {t("Delete_Headquarter")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {(showChangeDataModal && renderChangeDataModal()) ||
        (showCreateHospitalModal && renderCreateHospitalModal()) ||
        (showCreatePlanModal && renderCreatePlanModal()) ||
        (showConfirmDeleteHQModal && renderConfirmDeleteHQModal()) ||
        (showCreateHQAccountModal && renderCreateHQAccountModal())}
    </>
  );
};

export default HeadquarterMenu;
